import type { ListItem, Member } from '../lib/api'
import { MemberPicker } from './MemberPicker'

/** A CALL THAT ALREADY HAPPENED (078), the part of Take a call that replaces
 *  "Send": when it came in, who went and when, and how it ended.
 *
 *  The customer, 24 Oct 2026: "we still have some dispatching with pen and
 *  paper. And we just want to be able to log it ... who was there and
 *  everything, but no message needs to go out." The button is Save; the
 *  server (`POST /api/incidents/log-afterwards`) sends nothing at all.
 *
 *  TIMES ARE TYPED AS ON THE PAPER, hours and minutes, and placed on the day
 *  the call came in -- or the next day when earlier than that, since a call
 *  that came in at 23:50 is reached at 00:10 (`placeTime`). They are kept as
 *  typed and placed only when sent, so changing the date it came in moves
 *  every time with it. */

/** Saved with the draft (`IntakeDraft.past`). */
export type PastLog = {
  on: boolean
  /** The day and the time it came in, on this screen's clock. */
  date: string
  time: string
  went: { id: string; who: string; role: 'primary' | 'backup'; took: string; arrived: string }[]
  resolutionId: string
  resolutionLabel: string
  note: string
  closedAt: string
  /** "It is still going -- save it open." */
  open: boolean
}

function today(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function emptyPast(): PastLog {
  return {
    on: false, date: today(), time: '', went: [],
    resolutionId: '', resolutionLabel: '', note: '', closedAt: '', open: false,
  }
}

/** The instant a date and a time name, on this screen's clock. */
export function callInIso(date: string, time: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{1,2}:\d{2}$/.test(time)) return null
  const d = new Date(`${date}T${time.padStart(5, '0')}`)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

/** "21:43" placed on the day of `base`, or the day after when it is earlier
 *  than `base` -- a time on a paper log follows the call coming in. */
export function placeTime(hhmm: string, base: string | null): string | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim())
  if (!m || !base) return null
  const start = new Date(base)
  if (Number.isNaN(start.getTime())) return null
  const at = new Date(start)
  at.setHours(Number(m[1]), Number(m[2]), 0, 0)
  const floor = new Date(start)
  floor.setSeconds(0, 0)
  if (at < floor) at.setDate(at.getDate() + 1)
  // THE MINUTE IT CAME IN IS THE CALL'S OWN: a call in at 21:40:37 that a
  // member took "at 21:40" was taken as it came in, not 37 seconds before.
  else if (at < start) return start.toISOString()
  return at.toISOString()
}

/** "21:43" for an instant, on this screen's clock; '' for none. */
export function clockOf(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? ''
    : `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/** What still stops Save, in words, or null. The server refuses the same.
 *  NO TIME STOPS IT (083, the customer: "it should always save"): only the
 *  day it came in, and how it ended when it is closed. An empty time is
 *  warned about on Save (`pastTimesMissing`), never refused. */
export function pastMissing(p: PastLog): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(p.date)) return 'Needs the day it came in (step 4)'
  const calledIso = callInIso(p.date, p.time)
  if (calledIso && new Date(calledIso).getTime() > Date.now() + 5 * 60_000) return 'The time it came in is in the future'
  if (!p.open && !p.resolutionLabel.trim()) return 'Needs how it ended (step 4)'
  return null
}

/** The call's own times left empty, for the warning on Save (083): the
 *  member's are optional and say nothing. */
export function pastTimesMissing(p: PastLog): ('in' | 'closed')[] {
  return [
    ...(p.time.trim() ? [] : ['in' as const]),
    ...(!p.open && !p.closedAt.trim() ? ['closed' as const] : []),
  ]
}

/** WITH NO CAME-IN TIME (083), where the times typed are placed: on the day
 *  it came in, starting at the time that follows the LONGEST GAP round the
 *  clock -- the stretch of the day nothing on the paper happened in. So a
 *  call that ran past midnight (took 23:55, arrived 00:15, closed 00:40)
 *  keeps its order, as it does when a came-in time is typed (083 review). */
export function anchorOf(date: string, clocks: string[]): string | null {
  const mins = [...new Set(clocks.map((c) => {
    const m = /^(\d{1,2}):(\d{2})$/.exec(c.trim())
    return m ? Number(m[1]) * 60 + Number(m[2]) : null
  }).filter((n): n is number => n !== null))].sort((a, b) => a - b)
  if (!mins.length) return callInIso(date, '00:00')
  let start = mins[0]
  let gap = mins[0] + 1440 - mins[mins.length - 1]
  for (let i = 1; i < mins.length; i++) {
    if (mins[i] - mins[i - 1] > gap) { gap = mins[i] - mins[i - 1]; start = mins[i] }
  }
  const two = (n: number) => String(n).padStart(2, '0')
  return callInIso(date, `${two(Math.floor(start / 60))}:${two(start % 60)}`)
}

/** Where a time typed on this paper log is placed: after the time it came
 *  in, or -- not known -- after `anchorOf`'s first time on that day. */
function baseOf(p: PastLog): string | null {
  return callInIso(p.date, p.time) ?? anchorOf(p.date, [
    ...p.went.map((w) => w.took), ...p.went.map((w) => w.arrived),
    ...(p.open ? [] : [p.closedAt]),
  ])
}

/** What `/log-afterwards` adds to the body Take a call opens a call with.
 *  A time left empty is sent as none (083) -- never a guess. */
export function pastBody(p: PastLog): Record<string, unknown> {
  const calledIso = callInIso(p.date, p.time)
  const base = baseOf(p)
  return {
    called_at: calledIso,
    called_day: p.date,
    went: p.went.map((w) => ({
      member_id: w.id,
      role: w.role,
      accepted_at: w.took.trim() ? placeTime(w.took, base) : null,
      on_scene_at: w.arrived.trim() ? placeTime(w.arrived, base) : null,
    })),
    close: p.open ? null : {
      resolution_id: p.resolutionId || null,
      resolution_label: p.resolutionLabel.trim(),
      outcome_note: p.note.trim() || null,
      closed_at: p.closedAt.trim() ? placeTime(p.closedAt, base) : null,
    },
  }
}

/** The times in a line, for the ticket: "In 21:40 · took 21:43 · arrived
 *  22:05 · closed 22:31". */
export function pastLine(p: PastLog): string {
  const took = p.went.map((w) => w.took).filter(Boolean)
  const arrived = p.went.map((w) => w.arrived).filter(Boolean)
  const base = baseOf(p)
  const first = (times: string[]) => times
    .map((t) => ({ t, at: placeTime(t, base) ?? '' }))
    .sort((a, b) => a.at.localeCompare(b.at))[0]?.t
  return [
    p.time ? `In ${p.time}` : 'times not known',
    took.length ? `took ${first(took)}` : null,
    arrived.length ? `arrived ${first(arrived)}` : null,
    p.open ? 'still going' : p.closedAt ? `closed ${p.closedAt}` : null,
  ].filter(Boolean).join(' · ')
}

/** A time on a paper log: a box for hours and minutes, saying "next day"
 *  when it falls after midnight of the day `base` is on. */
export function ClockField({ value, base, onChange, label, required, kept }: {
  value: string
  base: string | null
  onChange: (hhmm: string) => void
  label: string
  required?: boolean
  /** The instant this box really holds, when that is not simply its text
   *  placed after `base` -- a correction's untouched time. The day is said
   *  from it, so a box never looks like the same day when it is not. */
  kept?: string | null
}) {
  const placed = kept !== undefined ? kept : value ? placeTime(value, base) : null
  const later = placed && base && new Date(placed).toDateString() !== new Date(base).toDateString()
  return (
    <span className="lgc-clock">
      <input type="time" value={value} aria-label={label} required={required}
             onChange={(e) => onChange(e.target.value)} />
      {later && (
        <small>{new Date(placed).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' })}</small>
      )}
    </span>
  )
}

/** Step 4 of Take a call, when it is a call that already happened. */
export function PastCallFields({ value, onChange, roster, resolutions }: {
  value: PastLog
  /** Handed a change OF THE LATEST VALUE, never a copy of this render's: two
   *  boxes changed before a redraw must not undo each other. */
  onChange: (update: (current: PastLog) => PastLog) => void
  roster: Member[]
  resolutions: ListItem[]
}) {
  const p = value
  const set = (patch: Partial<PastLog>) => onChange((cur) => ({ ...cur, ...patch }))
  const base = baseOf(p)
  const setWent = (id: string, patch: Partial<PastLog['went'][number]>) =>
    onChange((cur) => ({ ...cur, went: cur.went.map((w) => (w.id === id ? { ...w, ...patch } : w)) }))

  return (
    <div className="lgc">
      <div className="field">
        <label htmlFor="lgc-date">The call came in</label>
        <div className="lgc-when">
          <input id="lgc-date" type="date" value={p.date} aria-label="The day it came in"
                 onChange={(e) => set({ date: e.target.value })} />
          <input type="time" value={p.time} aria-label="The time it came in"
                 onChange={(e) => set({ time: e.target.value })} />
          {!p.time && <small className="lgc-unknown">time not known? leave it empty</small>}
        </div>
      </div>

      <div className="field">
        <label>Who went — pick from the roster, and the times written down, if known</label>
        {p.went.length > 0 && (
          <table className="lgc-who">
            <thead>
              <tr><th>Member</th><th>Role</th><th>Took it</th><th>Arrived</th><th /></tr>
            </thead>
            <tbody>
              {p.went.map((w) => (
                <tr key={w.id}>
                  <td className="lgc-nm">{w.who}</td>
                  <td>
                    <select value={w.role} aria-label={`${w.who}'s role`}
                            onChange={(e) => {
                              const role = e.target.value as 'primary' | 'backup'
                              // ONE PRIMARY: choosing him makes the last one a backup.
                              onChange((cur) => ({
                                ...cur,
                                went: cur.went.map((o) => (o.id === w.id ? { ...o, role }
                                  : role === 'primary' && o.role === 'primary' ? { ...o, role: 'backup' } : o)),
                              }))
                            }}>
                      <option value="primary">primary</option>
                      <option value="backup">backup</option>
                    </select>
                  </td>
                  <td><ClockField value={w.took} base={base} label={`When ${w.who} took it`}
                                  onChange={(took) => setWent(w.id, { took })} />
                      {!w.took && <small className="lgc-unknown">not known</small>}</td>
                  <td><ClockField value={w.arrived} base={base} label={`When ${w.who} arrived`}
                                  onChange={(arrived) => setWent(w.id, { arrived })} /></td>
                  <td>
                    <button type="button" className="ghost tiny" aria-label={`Take ${w.who} off the list`}
                            onClick={() => onChange((cur) => ({ ...cur, went: cur.went.filter((o) => o.id !== w.id) }))}>
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <MemberPicker
          roster={roster}
          exclude={p.went.map((w) => w.id)}
          label="Add a member who went"
          placeholder="+ Add a member who went — a unit, a name, or the last digits of a number"
          onPick={(id, who) => onChange((cur) => ({
            ...cur,
            went: [...cur.went, {
              id, who, role: cur.went.some((w) => w.role === 'primary') ? 'backup' : 'primary',
              took: '', arrived: '',
            }],
          }))}
        />
      </div>

      <label className="lgc-tick">
        <input type="checkbox" checked={p.open} onChange={(e) => set({ open: e.target.checked })} />
        <span>It is still going — save it open
          <small>It goes to Open calls instead of being closed. Nothing is sent either way.</small>
        </span>
      </label>

      {!p.open && (
        <>
          <div className="field">
            <label>How it ended</label>
            <div className="chips">
              {resolutions.filter((r) => !r.retired_at).map((r) => (
                <button type="button" key={r.id}
                        className={`chip ${p.resolutionId === r.id ? 'on' : ''}`}
                        onClick={() => (p.resolutionId === r.id
                          ? set({ resolutionId: '', resolutionLabel: '' })
                          : set({ resolutionId: r.id, resolutionLabel: r.name }))}>
                  {r.name}
                </button>
              ))}
            </div>
            {resolutions.filter((r) => !r.retired_at).length === 0 && (
              <input value={p.resolutionLabel} maxLength={120} aria-label="How it ended"
                     placeholder="How it ended"
                     onChange={(e) => set({ resolutionId: '', resolutionLabel: e.target.value })} />
            )}
          </div>
          <div className="lgc-close">
            <div className="field">
              <label htmlFor="lgc-note">Closing note</label>
              <input id="lgc-note" value={p.note} maxLength={4000}
                     onChange={(e) => set({ note: e.target.value })} />
            </div>
            <div className="field">
              <label>Closed at</label>
              <ClockField value={p.closedAt} base={base} label="When it closed"
                          onChange={(closedAt) => set({ closedAt })} />
              {!p.closedAt && <small className="lgc-unknown">time not known? leave it empty</small>}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
